import * as DialogPrimitive from '@radix-ui/react-dialog'
import { XIcon } from 'lucide-react'
import * as React from 'react'
import { cn } from '#app/utils/misc.tsx'

/**
 * Sheet — a slide-in panel built on top of Radix Dialog.
 *
 * API mirrors shadcn/ui's Sheet: same `side` prop, same sub-components.
 * Reuses `@radix-ui/react-dialog` which is already a project dependency.
 *
 * side="bottom" (default) → fixed inset-x-0 bottom-0, slides up.
 * side="right"            → fixed inset-y-0 right-0, slides in from right.
 */

// ---------------------------------------------------------------------------
// Root, Trigger, Portal, Close, Overlay — thin wrappers
// ---------------------------------------------------------------------------

function Sheet({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetPortal({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	return <DialogPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetClose({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetOverlay({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			data-slot="sheet-overlay"
			className={cn(
				'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
				className,
			)}
			{...props}
		/>
	)
}

// ---------------------------------------------------------------------------
// SheetContent
// ---------------------------------------------------------------------------

type SheetSide = 'bottom' | 'right'

const sheetContentVariants: Record<SheetSide, string> = {
	bottom:
		'data-[state=open]:animate-slide-top data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom inset-x-0 bottom-0 max-h-[90dvh] rounded-t-xl',
	right:
		'data-[state=open]:animate-slide-left data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right inset-y-0 right-0 w-full sm:max-w-sm',
}

function SheetContent({
	side = 'bottom',
	className,
	children,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
	side?: SheetSide
}) {
	return (
		<SheetPortal>
			<SheetOverlay />
			<DialogPrimitive.Content
				data-slot="sheet-content"
				className={cn(
					'bg-background fixed z-50 flex flex-col gap-4 overflow-y-auto border p-6 shadow-lg duration-300',
					sheetContentVariants[side],
					className,
				)}
				{...props}
			>
				{children}
				<DialogPrimitive.Close
					data-slot="sheet-close"
					className="ring-offset-background focus:ring-ring h-touch w-touch absolute top-1 right-1 flex items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
				>
					<XIcon className="size-4" />
					<span className="sr-only">Fermer</span>
				</DialogPrimitive.Close>
			</DialogPrimitive.Content>
		</SheetPortal>
	)
}

// ---------------------------------------------------------------------------
// SheetHeader / SheetTitle / SheetDescription
// ---------------------------------------------------------------------------

function SheetHeader({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="sheet-header"
			className={cn('flex flex-col gap-1.5', className)}
			{...props}
		/>
	)
}

function SheetTitle({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			data-slot="sheet-title"
			className={cn('text-foreground text-lg font-semibold', className)}
			{...props}
		/>
	)
}

function SheetDescription({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			data-slot="sheet-description"
			className={cn('text-muted-foreground text-sm', className)}
			{...props}
		/>
	)
}

export {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetOverlay,
	SheetPortal,
	SheetTitle,
	SheetTrigger,
}
